import { lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { Toaster } from '@/components/ui/sonner';
const Login = lazy(() => import('@/pages/Login'));
import AuthCallback from '@/pages/AuthCallback';
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const ParentMasteryPage = lazy(() => import('@/pages/parent/ParentMasteryPage'));
const ParentOnlineAnalytics = lazy(() => import('@/pages/parent/ParentOnlineAnalytics'));
const ParentOfflineAnalytics = lazy(() => import('@/pages/parent/ParentOfflineAnalytics'));
const ParentMonthlyFeedback = lazy(() => import('@/pages/parent/ParentMonthlyFeedback'));
const StudentProfile = lazy(() => import('@/pages/StudentProfile'));
const Feedback = lazy(() => import('@/pages/Feedback'));
const Tests = lazy(() => import('@/pages/Tests'));
const Attendance = lazy(() => import('@/pages/Attendance'));
const Homework = lazy(() => import('@/pages/Homework'));
const StudyMaterials = lazy(() => import('@/pages/StudyMaterials'));
const Schedule = lazy(() => import('@/pages/Schedule'));
const Messages = lazy(() => import('@/pages/Messages'));
const Announcements = lazy(() => import('@/pages/Announcements'));
const Tickets = lazy(() => import('@/pages/Tickets'));
const Fees = lazy(() => import('@/pages/Fees'));
const AcademicPlanner = lazy(() => import('@/pages/AcademicPlanner'));
const TestPlanner = lazy(() => import('@/pages/TestPlanner'));
const LectureNotes = lazy(() => import('@/pages/DailyClassUpdates'));
const Settings = lazy(() => import('@/pages/Settings'));
import ProtectedRoute from '@/components/ProtectedRoute';
const ClassNotesManager = lazy(() => import('@/pages/ClassNotesManager'));
const ClassNotesViewer = lazy(() => import('@/pages/ClassNotesViewer'));

// Admin Portal Pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const StudentProfilePage = lazy(() => import('@/pages/admin/StudentProfilePage'));
const BusinessAnalyticsDashboard = lazy(() => import('@/pages/admin/BusinessAnalyticsDashboard'));
const InstallmentRecovery = lazy(() => import('@/pages/admin/InstallmentRecovery'));
const AdminLogin = lazy(() => import('@/pages/admin/AdminLogin'));
const ManageCenters = lazy(() => import('@/pages/admin/ManageCenters'));
const ManageCourses = lazy(() => import('@/pages/admin/ManageCourses'));
const ManageEnrollments = lazy(() => import('@/pages/admin/ManageEnrollments'));
const BulkEnrollmentImport = lazy(() => import('@/pages/admin/BulkEnrollmentImport'));
const ManageBatches = lazy(() => import('@/pages/admin/ManageBatches'));
const PromoCodesPage = lazy(() => import('@/pages/admin/PromoCodesPage'));
const RenewalManagement = lazy(() => import('@/pages/admin/RenewalManagement'));
const ManageSchedule = lazy(() => import('@/pages/admin/ManageSchedule'));
const TeacherShiftsPage = lazy(() => import('@/pages/admin/TeacherShiftsPage'));
const StaffAttendanceReport = lazy(() => import('@/pages/admin/StaffAttendanceReport'));
const StaffPerformanceReport = lazy(() => import('@/pages/admin/StaffPerformanceReport'));
const MyAttendance = lazy(() => import('@/pages/MyAttendance'));
const TeacherOfMonthAdmin = lazy(() => import('@/pages/admin/TeacherOfMonthAdmin'));
const PartnerPermissionsPage = lazy(() => import('@/pages/admin/PartnerPermissionsPage'));
const BatchPaceTracker = lazy(() => import('@/pages/admin/BatchPaceTracker'));
const StudentFeedbackAudit = lazy(() => import('@/pages/admin/StudentFeedbackAudit'));
const HeadOfficeFinance = lazy(() => import('@/pages/headoffice/HeadOfficeFinance'));
const NonCashLedger = lazy(() => import('@/pages/accounts/NonCashLedger'));
const AvgSalesPriceReport = lazy(() => import('@/pages/accounts/AvgSalesPriceReport'));
const FinanceMasters = lazy(() => import('@/pages/admin/FinanceMasters'));
const GlobalBatches = lazy(() => import('@/pages/admin/GlobalBatches'));
const AcademicPlans = lazy(() => import('@/pages/admin/AcademicPlans'));
import ActiveCenterPill from '@/components/admin/ActiveCenterPill';
import GlobalHomeButton from '@/components/GlobalHomeButton';
import GlobalStaffAttendanceGate from '@/components/GlobalStaffAttendanceGate';
import TeacherOfMonthCelebration from '@/components/TeacherOfMonthCelebration';
const MasterEditor = lazy(() => import('@/pages/admin/MasterEditor'));
const InventoryDashboard = lazy(() => import('@/pages/inventory/InventoryDashboard'));
const InstallmentReminders = lazy(() => import('@/pages/admin/InstallmentReminders'));
const FinancialReports = lazy(() => import('@/pages/admin/FinancialReports'));
const ManageTeachers = lazy(() => import('@/pages/admin/ManageTeachers'));
const ManageAdmins = lazy(() => import('@/pages/admin/ManageAdmins'));
const MicrosoftEmailMapping = lazy(() => import('@/pages/admin/MicrosoftEmailMapping'));
const RejectedPaymentsPage = lazy(() => import('@/pages/admin/RejectedPaymentsPage'));
const PendingPaymentsPage = lazy(() => import('@/pages/admin/PendingPaymentsPage'));
const TeacherAvailabilityPage = lazy(() => import('@/components/admin/TeacherAvailability').then(m => ({ default: m.TeacherAvailabilityPage })));
const FxAdjustmentsPage = lazy(() => import('@/pages/admin/FxAdjustmentsPage'));
const BatchStudents = lazy(() => import('@/pages/admin/BatchStudents'));
const AdminAnnouncements = lazy(() => import('@/pages/admin/Announcements'));
const StudentPerformance = lazy(() => import('@/pages/admin/StudentPerformance'));
const CalendarEvents = lazy(() => import('@/pages/admin/CalendarEvents'));
const AdminHRTickets = lazy(() => import('@/pages/admin/AdminHRTickets'));
const RecognitionManagement = lazy(() => import('@/pages/admin/RecognitionManagement'));
const ConsecutiveAbsences = lazy(() => import('@/pages/admin/ConsecutiveAbsences'));
const BulkImport = lazy(() => import('@/pages/admin/BulkImport'));
const SystemSettings = lazy(() => import('@/pages/admin/SystemSettings'));
const ReferralSettingsPage = lazy(() => import('@/pages/admin/ReferralSettingsPage'));
const ExpenseManager = lazy(() => import('@/pages/admin/ExpenseManager'));
const SuspenseClaiming = lazy(() => import('@/pages/admin/SuspenseClaiming'));
const ExpenseCategories = lazy(() => import('@/pages/admin/ExpenseCategories'));
const RefundHistory = lazy(() => import('@/pages/admin/RefundHistory'));
const ManageStudents = lazy(() => import('@/pages/admin/ManageStudents'));
const AttendanceDashboard = lazy(() => import('@/pages/admin/AttendanceDashboard'));
const StorageDashboard = lazy(() => import('@/pages/admin/StorageDashboard'));
const TeacherReportingManagers = lazy(() => import('@/pages/admin/TeacherReportingManagers'));
const AdminTimeUtilization = lazy(() => import('@/pages/admin/AdminTimeUtilization'));
const MasterTopics = lazy(() => import('@/pages/admin/MasterTopics'));
const ParentFeedbackManagement = lazy(() => import('@/pages/admin/ParentFeedbackManagement'));
const SelfStudyKitManager = lazy(() => import('@/pages/admin/SelfStudyKitManager'));
const StudentsNeedingAttentionPage = lazy(() => import('@/pages/admin/StudentsNeedingAttentionPage'));

// Lead Management Pages
const LeadDashboard = lazy(() => import('@/pages/leads/LeadDashboard'));
const LeadDetail = lazy(() => import('@/pages/leads/LeadDetail'));
const UpcomingFollowups = lazy(() => import('@/pages/leads/UpcomingFollowups'));
const LeadBulkUpload = lazy(() => import('@/pages/leads/LeadBulkUpload'));
const LeadsTodayActivity = lazy(() => import('@/pages/leads/LeadsTodayActivity'));
const LeadLeaderboard = lazy(() => import('@/pages/leads/LeadLeaderboard'));

// HR Portal Pages
const HRDashboard = lazy(() => import('@/pages/hr/HRDashboard'));

// Head Office Portal Pages
const HeadOfficeDashboard = lazy(() => import('@/pages/headoffice/HeadOfficeDashboard'));

// Accounts Team Portal Pages
const AccountsDashboard = lazy(() => import('@/pages/accounts/AccountsDashboard'));
const PaymentReconciliation = lazy(() => import('@/pages/accounts/PaymentReconciliation'));

// Parent Portal Pages
const ParentReferrals = lazy(() => import('@/pages/ParentReferrals'));
const MonthlyFeedback = lazy(() => import('@/pages/MonthlyFeedback'));

// Teacher Portal Pages
const TeacherDashboard = lazy(() => import('@/pages/teacher/TeacherDashboard'));
const TeacherRenewals = lazy(() => import('@/pages/teacher/TeacherRenewals'));
const TeacherLogin = lazy(() => import('@/pages/teacher/TeacherLogin'));
const MarkAttendance = lazy(() => import('@/pages/teacher/MarkAttendance'));
const AssignHomework = lazy(() => import('@/pages/teacher/AssignHomework'));
const UploadFeedback = lazy(() => import('@/pages/teacher/UploadFeedback'));
// iter 254 — Center Manager feedback (mirrors teacher flow, no monthly cap)
const CMUploadFeedback = lazy(() => import('@/pages/centermanager/CMUploadFeedback'));
// iter 259 — Online CM books 1-on-1 on behalf of parent
const CMBook1on1 = lazy(() => import('@/pages/centermanager/CMBook1on1'));
// iter 255 — Doubts Data Board (SA + CM)
const DoubtsBoard = lazy(() => import('@/pages/admin/DoubtsBoard'));
const TeacherTests = lazy(() => import('@/pages/teacher/TeacherTests'));
const BatchDetails = lazy(() => import('@/pages/teacher/BatchDetails'));
const TeacherMessages = lazy(() => import('@/pages/teacher/TeacherMessages'));
const BulkTestScores = lazy(() => import('@/pages/teacher/BulkTestScores'));
const DoubtSessions = lazy(() => import('@/pages/teacher/DoubtSessions'));
const TeacherForum = lazy(() => import('@/pages/teacher/TeacherForum'));
const TeacherStudyMaterials = lazy(() => import('@/pages/teacher/TeacherStudyMaterials'));
const TeacherDailyUpdates = lazy(() => import('@/pages/teacher/TeacherDailyUpdates'));
const TeacherStudentMastery = lazy(() => import('@/pages/teacher/TeacherStudentMastery'));
const HRTickets = lazy(() => import('@/pages/teacher/HRTickets'));
const AssignedTickets = lazy(() => import('@/pages/teacher/AssignedTickets'));
const TeacherAcademicPlanner = lazy(() => import('@/pages/teacher/TeacherAcademicPlanner'));
const HomeworkReview = lazy(() => import('@/pages/teacher/HomeworkReview'));
const HomeworkList = lazy(() => import('@/pages/teacher/HomeworkList'));
const TimeUtilization = lazy(() => import('@/pages/teacher/TimeUtilization'));
const TimeUtilizationApprovals = lazy(() => import('@/pages/teacher/TimeUtilizationApprovals'));
const TeacherDoubts = lazy(() => import('@/pages/teacher/TeacherDoubts'));
const TeacherMyFeedback = lazy(() => import('@/pages/teacher/TeacherMyFeedback'));
const TeacherBatches = lazy(() => import('@/pages/teacher/TeacherBatches'));
const TeacherPayables = lazy(() => import('@/pages/teacher/TeacherPayables'));
const TeacherUserGuide = lazy(() => import('@/pages/teacher/TeacherUserGuide'));
const StarStudentsPage = lazy(() => import('@/pages/StarStudents'));
const StudentMoodsPage = lazy(() => import('@/pages/StudentMoods'));
const WeakStudentsPage = lazy(() => import('@/pages/WeakStudents'));
const ParentUserGuide = lazy(() => import('@/pages/parent/ParentUserGuide'));
const StudentUserGuide = lazy(() => import('@/pages/student/StudentUserGuide'));
const AdminUserGuide = lazy(() => import('@/pages/admin/AdminUserGuide'));
const BillApproval = lazy(() => import('@/pages/admin/BillApproval'));
const SuperAdminBillApprovals = lazy(() => import('@/pages/admin/SuperAdminBillApprovals'));

// Student Portal Pages
const StudentDashboard = lazy(() => import('@/pages/student/StudentDashboard'));
const StudentTests = lazy(() => import('@/pages/student/StudentTests'));
const PeerRankings = lazy(() => import('@/pages/student/PeerRankings'));
const StudentHomework = lazy(() => import('@/pages/student/StudentHomework'));
const DiscussionForum = lazy(() => import('@/pages/student/DiscussionForum'));
const Student1on1Dashboard = lazy(() => import('@/pages/student/Student1on1Dashboard'));
const StudentDoubts = lazy(() => import('@/pages/student/StudentDoubts'));
const StudentBatchSolvedDoubts = lazy(() => import('@/pages/student/StudentBatchSolvedDoubts'));
const MyAttendanceV2 = lazy(() => import('@/pages/attendance/MyAttendance'));
const StaffAttendanceReportV2 = lazy(() => import('@/pages/attendance/StaffAttendanceReport'));
const StudentMasteryPage = lazy(() => import('@/pages/student/StudentMasteryPage'));
const SelfStudyKit = lazy(() => import('@/pages/student/SelfStudyKit'));

// 1-on-1 Classes Module
const Teacher1on1Dashboard = lazy(() => import('@/pages/one_on_one/Teacher1on1Dashboard'));
const Parent1on1Dashboard = lazy(() => import('@/pages/one_on_one/Parent1on1Dashboard'));
const OneOnOneManagement = lazy(() => import('@/pages/admin/OneOnOneManagement'));
const OneOnOneStudentsPage = lazy(() => import('@/pages/admin/OneOnOneStudentsPage'));
const MentoringReport = lazy(() => import('@/pages/admin/MentoringReport'));
const ClassArchive = lazy(() => import('@/pages/admin/ClassArchive'));
const TestSchedule = lazy(() => import('@/pages/admin/TestSchedule'));
const ComplaintCenter = lazy(() => import('@/pages/admin/ComplaintCenter'));
const ReminderSettings = lazy(() => import('@/pages/admin/ReminderSettings'));

// HRMS Module
const HRPortalDashboard = lazy(() => import('@/pages/hrms/HRPortalDashboard'));
const EmployeeHRMSDashboard = lazy(() => import('@/pages/hrms/EmployeeHRMSDashboard'));
const CareersPage = lazy(() => import('@/pages/hrms/CareersPage'));
const RecruiterPortalDashboard = lazy(() => import('@/pages/hrms/RecruiterPortalDashboard'));
const HRMSLogin = lazy(() => import('@/pages/hrms/HRMSLogin'));
const AccountsLogin = lazy(() => import('@/pages/accounts/AccountsLogin'));

// Online Testing Module
const QuestionBank = lazy(() => import('@/pages/tests/QuestionBank'));
const TestManagement = lazy(() => import('@/pages/tests/TestManagement'));
const TestEditor = lazy(() => import('@/pages/tests/TestEditor'));
const TestInterface = lazy(() => import('@/pages/tests/TestInterface'));
const TestResults = lazy(() => import('@/pages/tests/TestResults'));
const StudentTestsDashboard = lazy(() => import('@/pages/tests/StudentTestsDashboard'));
const AnalyticsDashboard = lazy(() => import('@/pages/tests/AnalyticsDashboard'));
const ProctoringDashboard = lazy(() => import('@/pages/tests/ProctoringDashboard'));
const FlaggedQuestions = lazy(() => import('@/pages/tests/FlaggedQuestions'));

// Notebook Module (Custom Class Notes)
const NotebookEditor = lazy(() => import('@/pages/NotebookEditor'));

// Partner Portal
const PartnerLogin = lazy(() => import('@/pages/partner/PartnerLogin'));
const PreSalesLogin = lazy(() => import('@/pages/presales/PreSalesLogin'));
const PartnerLayout = lazy(() => import('@/pages/partner/PartnerLayout'));
const PartnerDashboard = lazy(() => import('@/pages/partner/PartnerDashboard'));
const PartnerEnrollments = lazy(() => import('@/pages/partner/PartnerEnrollments'));
const PartnerFees = lazy(() => import('@/pages/partner/PartnerFees'));
const PartnerRefunds = lazy(() => import('@/pages/partner/PartnerRefunds'));
const PartnerBadDebt = lazy(() => import('@/pages/partner/PartnerBadDebt'));
const PartnerStudents = lazy(() => import('@/pages/partner/PartnerStudents'));
const PartnerDrilldown = lazy(() => import('@/pages/partner/PartnerDrilldown'));

// User Onboarding Status Report (CA / CM)
const UserOnboardingReport = lazy(() => import('@/pages/admin/UserOnboardingReport'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950" data-testid="route-loader">
      <div className="w-8 h-8 rounded-full border-2 border-slate-700 border-t-cyan-400 animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/teacher/login" element={<TeacherLogin />} />
      <Route path="/hrms/login" element={<HRMSLogin />} />
      <Route path="/accounts/login" element={<AccountsLogin />} />
      <Route path="/partner/login" element={<PartnerLogin />} />
      <Route path="/presales" element={<PreSalesLogin />} />
      <Route path="/presales/login" element={<PreSalesLogin />} />

      {/* Partner Portal — read-only multi-center business view */}
      <Route path="/partner" element={
        <ProtectedRoute allowedRoles={['partner', 'super_admin', 'head_office', 'headoffice_admin']}>
          <PartnerLayout />
        </ProtectedRoute>
      }>
        <Route index element={<PartnerDashboard />} />
        <Route path="enrollments" element={<PartnerEnrollments />} />
        <Route path="fees" element={<PartnerFees />} />
        <Route path="refunds" element={<PartnerRefunds />} />
        <Route path="bad-debt" element={<PartnerBadDebt />} />
        <Route path="students" element={<PartnerStudents />} />
        <Route path="list/:tile" element={<PartnerDrilldown />} />
      </Route>
      <Route path="/" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/parent" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/parent/mastery" element={
        <ProtectedRoute>
          <ParentMasteryPage />
        </ProtectedRoute>
      } />
      <Route path="/parent/feedback" element={
        <ProtectedRoute>
          <ParentMonthlyFeedback />
        </ProtectedRoute>
      } />
      <Route path="/parent/online-analytics" element={
        <ProtectedRoute>
          <ParentOnlineAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/parent/offline-analytics" element={
        <ProtectedRoute>
          <ParentOfflineAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/student-profile" element={
        <ProtectedRoute>
          <StudentProfile />
        </ProtectedRoute>
      } />
      <Route path="/mentor-feedback" element={
        <ProtectedRoute>
          <Feedback />
        </ProtectedRoute>
      } />
      <Route path="/lecture-notes" element={
        <ProtectedRoute>
          <LectureNotes />
        </ProtectedRoute>
      } />
      
      {/* Online Testing System Routes - Must be before /tests to avoid route conflict */}
      <Route path="/tests/questions" element={
        <ProtectedRoute>
          <QuestionBank />
        </ProtectedRoute>
      } />
      <Route path="/tests/manage" element={
        <ProtectedRoute>
          <TestManagement />
        </ProtectedRoute>
      } />
      <Route path="/tests/edit/:testId" element={
        <ProtectedRoute>
          <TestEditor />
        </ProtectedRoute>
      } />
      <Route path="/tests/take/:testId" element={
        <ProtectedRoute>
          <TestInterface />
        </ProtectedRoute>
      } />
      <Route path="/tests/results/:attemptId" element={
        <ProtectedRoute>
          <TestResults />
        </ProtectedRoute>
      } />
      <Route path="/tests/analytics" element={
        <ProtectedRoute>
          <AnalyticsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/tests/proctoring" element={
        <ProtectedRoute>
          <ProctoringDashboard />
        </ProtectedRoute>
      } />
      <Route path="/tests/flagged" element={
        <ProtectedRoute>
          <FlaggedQuestions />
        </ProtectedRoute>
      } />
      <Route path="/tests/student" element={
        <ProtectedRoute>
          <StudentTestsDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/tests" element={
        <ProtectedRoute>
          <Tests />
        </ProtectedRoute>
      } />
      <Route path="/attendance" element={
        <ProtectedRoute>
          <Attendance />
        </ProtectedRoute>
      } />
      <Route path="/homework" element={
        <ProtectedRoute>
          <Homework />
        </ProtectedRoute>
      } />
      <Route path="/study-materials" element={
        <ProtectedRoute>
          <StudyMaterials />
        </ProtectedRoute>
      } />
      <Route path="/schedule" element={
        <ProtectedRoute>
          <Schedule />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/announcements" element={
        <ProtectedRoute>
          <Announcements />
        </ProtectedRoute>
      } />
      <Route path="/tickets" element={
        <ProtectedRoute>
          <Tickets />
        </ProtectedRoute>
      } />
      <Route path="/fees" element={
        <ProtectedRoute>
          <Fees />
        </ProtectedRoute>
      } />
      <Route path="/academic-planner" element={
        <ProtectedRoute>
          <AcademicPlanner />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/referrals" element={
        <ProtectedRoute>
          <ParentReferrals />
        </ProtectedRoute>
      } />
      <Route path="/monthly-feedback" element={
        <ProtectedRoute>
          <MonthlyFeedback />
        </ProtectedRoute>
      } />
      
      {/* Admin Portal Routes */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/centers" element={
        <ProtectedRoute>
          <ManageCenters />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses" element={
        <ProtectedRoute>
          <ManageCourses />
        </ProtectedRoute>
      } />
      <Route path="/admin/enrollments" element={
        <ProtectedRoute>
          <ManageEnrollments />
        </ProtectedRoute>
      } />
      <Route path="/admin/fees" element={
        <ProtectedRoute>
          <ManageEnrollments />
        </ProtectedRoute>
      } />
      <Route path="/admin/bulk-import" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <BulkEnrollmentImport />
        </ProtectedRoute>
      } />
      <Route path="/admin/master-editor" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <MasterEditor />
        </ProtectedRoute>
      } />
      <Route path="/inventory" element={
        <ProtectedRoute allowedRoles={['super_admin', 'headoffice_admin', 'head_office', 'center_manager', 'center_admin']}>
          <InventoryDashboard />
        </ProtectedRoute>
      } />
      <Route path="/headoffice/inventory" element={
        <ProtectedRoute allowedRoles={['super_admin', 'headoffice_admin', 'head_office']}>
          <InventoryDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/batches" element={
        <ProtectedRoute>
          <ManageBatches />
        </ProtectedRoute>
      } />
      <Route path="/admin/promo-codes" element={
        <ProtectedRoute allowedRoles={['super_admin', 'center_manager', 'center_admin', 'head_office', 'headoffice_admin', 'accounts']}>
          <PromoCodesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/batch/:batchId/students" element={
        <ProtectedRoute>
          <BatchStudents />
        </ProtectedRoute>
      } />
      <Route path="/admin/schedule" element={
        <ProtectedRoute>
          <ManageSchedule />
        </ProtectedRoute>
      } />
      <Route path="/admin/teacher-shifts" element={
        <ProtectedRoute>
          <TeacherShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/hrms/teacher-shifts" element={
        <ProtectedRoute>
          <TeacherShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/staff-attendance" element={
        <ProtectedRoute>
          <StaffAttendanceReport />
        </ProtectedRoute>
      } />
      <Route path="/hrms/staff-attendance" element={
        <ProtectedRoute>
          <StaffAttendanceReport />
        </ProtectedRoute>
      } />
      <Route path="/admin/staff-performance" element={
        <ProtectedRoute>
          <StaffPerformanceReport />
        </ProtectedRoute>
      } />
      <Route path="/hrms/staff-performance" element={
        <ProtectedRoute>
          <StaffPerformanceReport />
        </ProtectedRoute>
      } />
      <Route path="/my-attendance" element={
        <ProtectedRoute>
          <MyAttendance />
        </ProtectedRoute>
      } />
      <Route path="/admin/teacher-of-month" element={
        <ProtectedRoute>
          <TeacherOfMonthAdmin />
        </ProtectedRoute>
      } />
      <Route path="/admin/partner-permissions" element={
        <ProtectedRoute>
          <PartnerPermissionsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/batch-pace-tracker" element={
        <ProtectedRoute>
          <BatchPaceTracker />
        </ProtectedRoute>
      } />
      <Route path="/admin/global-batches" element={
        <ProtectedRoute>
          <GlobalBatches />
        </ProtectedRoute>
      } />
      <Route path="/admin/onboarding-report" element={
        <ProtectedRoute allowedRoles={['super_admin', 'head_office', 'headoffice_admin', 'center_admin', 'center_manager']}>
          <UserOnboardingReport />
        </ProtectedRoute>
      } />
      <Route path="/admin/academic-plans" element={
        <ProtectedRoute>
          <AcademicPlans />
        </ProtectedRoute>
      } />
      <Route path="/admin/student-feedback-audit" element={
        <ProtectedRoute>
          <StudentFeedbackAudit />
        </ProtectedRoute>
      } />
      <Route path="/headoffice/finance" element={
        <ProtectedRoute>
          <HeadOfficeFinance />
        </ProtectedRoute>
      } />
      <Route path="/accounts/non-cash-ledger" element={
        <ProtectedRoute>
          <NonCashLedger />
        </ProtectedRoute>
      } />
      <Route path="/accounts/avg-sales-price" element={
        <ProtectedRoute>
          <AvgSalesPriceReport />
        </ProtectedRoute>
      } />
      <Route path="/admin/finance-masters" element={
        <ProtectedRoute>
          <FinanceMasters />
        </ProtectedRoute>
      } />
      <Route path="/admin/storage" element={
        <ProtectedRoute>
          <StorageDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/reminders" element={
        <ProtectedRoute>
          <InstallmentReminders />
        </ProtectedRoute>
      } />
      <Route path="/admin/financial-reports" element={
        <ProtectedRoute>
          <FinancialReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/installment-recovery" element={
        <ProtectedRoute>
          <InstallmentRecovery />
        </ProtectedRoute>
      } />
      <Route path="/admin/teachers" element={
        <ProtectedRoute>
          <ManageTeachers />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <ManageAdmins />
        </ProtectedRoute>
      } />
      <Route path="/admin/microsoft-mapping" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <MicrosoftEmailMapping />
        </ProtectedRoute>
      } />
      <Route path="/admin/rejected-payments" element={
        <ProtectedRoute>
          <RejectedPaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pending-payments" element={
        <ProtectedRoute>
          <PendingPaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/teacher-availability" element={
        <ProtectedRoute>
          <TeacherAvailabilityPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/fx-adjustments" element={
        <ProtectedRoute>
          <FxAdjustmentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/announcements" element={
        <ProtectedRoute>
          <AdminAnnouncements />
        </ProtectedRoute>
      } />
      <Route path="/admin/student-performance/:studentId" element={
        <ProtectedRoute>
          <StudentPerformance />
        </ProtectedRoute>
      } />
      <Route path="/teacher/student-performance/:studentId" element={
        <ProtectedRoute>
          <StudentPerformance />
        </ProtectedRoute>
      } />
      <Route path="/admin/student-profile/:studentId" element={
        <ProtectedRoute>
          <StudentProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/renewals" element={
        <ProtectedRoute>
          <RenewalManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute>
          <BusinessAnalyticsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/calendar-events" element={
        <ProtectedRoute>
          <CalendarEvents />
        </ProtectedRoute>
      } />
      <Route path="/admin/hr-tickets" element={
        <ProtectedRoute>
          <AdminHRTickets />
        </ProtectedRoute>
      } />
      <Route path="/admin/recognition" element={
        <ProtectedRoute>
          <RecognitionManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/absences" element={
        <ProtectedRoute>
          <ConsecutiveAbsences />
        </ProtectedRoute>
      } />
      <Route path="/admin/bulk-import" element={
        <ProtectedRoute>
          <BulkImport />
        </ProtectedRoute>
      } />
      <Route path="/admin/system-settings" element={
        <ProtectedRoute>
          <SystemSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/referral-settings" element={
        <ProtectedRoute allowedRoles={['super_admin', 'head_office']}>
          <ReferralSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/expenses" element={
        <ProtectedRoute>
          <ExpenseManager />
        </ProtectedRoute>
      } />
      <Route path="/admin/suspense-claiming" element={
        <ProtectedRoute>
          <SuspenseClaiming />
        </ProtectedRoute>
      } />
      <Route path="/admin/expense-categories" element={
        <ProtectedRoute>
          <ExpenseCategories />
        </ProtectedRoute>
      } />
      <Route path="/admin/refund-history" element={
        <ProtectedRoute>
          <RefundHistory />
        </ProtectedRoute>
      } />
      <Route path="/admin/students" element={
        <ProtectedRoute>
          <ManageStudents />
        </ProtectedRoute>
      } />
      <Route path="/admin/attendance" element={
        <ProtectedRoute>
          <AttendanceDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/teacher-reporting-managers" element={
        <ProtectedRoute>
          <TeacherReportingManagers />
        </ProtectedRoute>
      } />
      <Route path="/admin/time-utilization" element={
        <ProtectedRoute>
          <AdminTimeUtilization />
        </ProtectedRoute>
      } />
      <Route path="/admin/master-topics" element={
        <ProtectedRoute>
          <MasterTopics />
        </ProtectedRoute>
      } />
      <Route path="/admin/parent-feedback" element={
        <ProtectedRoute>
          <ParentFeedbackManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/self-study-kits" element={
        <ProtectedRoute>
          <SelfStudyKitManager />
        </ProtectedRoute>
      } />
      <Route path="/students-attention" element={
        <ProtectedRoute>
          <StudentsNeedingAttentionPage />
        </ProtectedRoute>
      } />
      
      {/* Lead Management Routes */}
      <Route path="/leads" element={
        <ProtectedRoute>
          <LeadDashboard />
        </ProtectedRoute>
      } />
      <Route path="/leads/bulk-upload" element={
        <ProtectedRoute>
          <LeadBulkUpload />
        </ProtectedRoute>
      } />
      <Route path="/leads/todays-activity" element={
        <ProtectedRoute>
          <LeadsTodayActivity />
        </ProtectedRoute>
      } />
      <Route path="/leads/upcoming-followups" element={
        <ProtectedRoute>
          <UpcomingFollowups />
        </ProtectedRoute>
      } />
      <Route path="/leads/leaderboard" element={
        <ProtectedRoute>
          <LeadLeaderboard />
        </ProtectedRoute>
      } />
      <Route path="/leads/:leadId" element={
        <ProtectedRoute>
          <LeadDetail />
        </ProtectedRoute>
      } />
      
      {/* Class Notes (OneNote) Routes */}
      <Route path="/class-notes" element={
        <ProtectedRoute>
          <ClassNotesManager />
        </ProtectedRoute>
      } />
      <Route path="/class-notes/view/:noteId" element={
        <ProtectedRoute>
          <ClassNotesViewer />
        </ProtectedRoute>
      } />
      
      {/* Notebook Editor (Custom Class Notes) */}
      <Route path="/notebook/:notebookId" element={
        <ProtectedRoute>
          <NotebookEditor />
        </ProtectedRoute>
      } />
      
      {/* HR Portal Routes */}
      <Route path="/hr" element={
        <ProtectedRoute>
          <HRDashboard />
        </ProtectedRoute>
      } />
      
      {/* Head Office Portal Routes */}
      <Route path="/headoffice" element={
        <ProtectedRoute>
          <HeadOfficeDashboard />
        </ProtectedRoute>
      } />
      
      {/* Accounts Team Portal Routes */}
      <Route path="/accounts" element={
        <ProtectedRoute>
          <AccountsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/payment-reconciliation" element={
        <ProtectedRoute>
          <PaymentReconciliation />
        </ProtectedRoute>
      } />
      <Route path="/accounts/payment-reconciliation" element={
        <ProtectedRoute>
          <PaymentReconciliation />
        </ProtectedRoute>
      } />
      
      {/* Teacher Portal Routes */}
      <Route path="/teacher" element={
        <ProtectedRoute>
          <TeacherDashboard />
        </ProtectedRoute>
      } />
      <Route path="/teacher/renewals" element={
        <ProtectedRoute>
          <TeacherRenewals />
        </ProtectedRoute>
      } />
      <Route path="/teacher/attendance" element={
        <ProtectedRoute>
          <MarkAttendance />
        </ProtectedRoute>
      } />
      <Route path="/teacher/homework" element={
        <ProtectedRoute>
          <AssignHomework />
        </ProtectedRoute>
      } />
      <Route path="/teacher/homework-review" element={
        <ProtectedRoute>
          <HomeworkList />
        </ProtectedRoute>
      } />
      <Route path="/teacher/homework-review/:submissionId" element={
        <ProtectedRoute>
          <HomeworkReview />
        </ProtectedRoute>
      } />
      <Route path="/teacher/homework/review/:submissionId" element={
        <ProtectedRoute>
          <HomeworkReview />
        </ProtectedRoute>
      } />
      <Route path="/teacher/time-utilization" element={
        <ProtectedRoute>
          <TimeUtilization />
        </ProtectedRoute>
      } />
      <Route path="/teacher/time-utilization-approvals" element={
        <ProtectedRoute>
          <TimeUtilizationApprovals />
        </ProtectedRoute>
      } />
      <Route path="/teacher/doubts" element={
        <ProtectedRoute>
          <TeacherDoubts />
        </ProtectedRoute>
      } />
      <Route path="/teacher/my-feedback" element={
        <ProtectedRoute>
          <TeacherMyFeedback />
        </ProtectedRoute>
      } />
      <Route path="/teacher/feedback" element={
        <ProtectedRoute>
          <UploadFeedback />
        </ProtectedRoute>
      } />
      <Route path="/cm/feedback" element={
        <ProtectedRoute allowedRoles={['super_admin', 'center_manager', 'center_admin']}>
          <CMUploadFeedback />
        </ProtectedRoute>
      } />
      <Route path="/cm/book-1on1" element={
        <ProtectedRoute allowedRoles={['super_admin', 'center_manager', 'center_admin']}>
          <CMBook1on1 />
        </ProtectedRoute>
      } />
      <Route path="/admin/doubts-board" element={
        <ProtectedRoute allowedRoles={['super_admin', 'head_office', 'headoffice_admin', 'center_manager', 'center_admin']}>
          <DoubtsBoard />
        </ProtectedRoute>
      } />
      <Route path="/teacher/tests" element={
        <ProtectedRoute>
          <TeacherTests />
        </ProtectedRoute>
      } />
      <Route path="/teacher/batch/:batchId" element={
        <ProtectedRoute>
          <BatchDetails />
        </ProtectedRoute>
      } />
      <Route path="/teacher/messages" element={
        <ProtectedRoute>
          <TeacherMessages />
        </ProtectedRoute>
      } />
      <Route path="/teacher/bulk-scores" element={
        <ProtectedRoute>
          <BulkTestScores />
        </ProtectedRoute>
      } />
      <Route path="/teacher/student/:studentId/mastery" element={
        <ProtectedRoute>
          <TeacherStudentMastery />
        </ProtectedRoute>
      } />
      <Route path="/teacher/doubt-sessions" element={
        <ProtectedRoute>
          <DoubtSessions />
        </ProtectedRoute>
      } />
      <Route path="/teacher/forum" element={
        <ProtectedRoute>
          <TeacherForum />
        </ProtectedRoute>
      } />
      <Route path="/teacher/study-materials" element={
        <ProtectedRoute>
          <TeacherStudyMaterials />
        </ProtectedRoute>
      } />
      <Route path="/teacher/daily-updates" element={
        <ProtectedRoute>
          <TeacherDailyUpdates />
        </ProtectedRoute>
      } />
      <Route path="/teacher/hr-tickets" element={
        <ProtectedRoute>
          <HRTickets />
        </ProtectedRoute>
      } />
      <Route path="/teacher/assigned-tickets" element={
        <ProtectedRoute>
          <AssignedTickets />
        </ProtectedRoute>
      } />
      <Route path="/teacher/academic-planner" element={
        <ProtectedRoute>
          <TeacherAcademicPlanner />
        </ProtectedRoute>
      } />
      <Route path="/teacher/test-planner" element={
        <ProtectedRoute>
          <TestPlanner />
        </ProtectedRoute>
      } />
      <Route path="/admin/test-planner" element={
        <ProtectedRoute>
          <TestPlanner />
        </ProtectedRoute>
      } />
      <Route path="/teacher/batches" element={
        <ProtectedRoute>
          <TeacherBatches />
        </ProtectedRoute>
      } />
      <Route path="/star-students" element={
        <ProtectedRoute>
          <StarStudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/star-students" element={
        <ProtectedRoute>
          <StarStudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/star-students" element={
        <ProtectedRoute>
          <StarStudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/student-moods" element={
        <ProtectedRoute>
          <StudentMoodsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/student-moods" element={
        <ProtectedRoute>
          <StudentMoodsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/student-moods" element={
        <ProtectedRoute>
          <StudentMoodsPage />
        </ProtectedRoute>
      } />
      <Route path="/weak-students" element={
        <ProtectedRoute>
          <WeakStudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/weak-students" element={
        <ProtectedRoute>
          <WeakStudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/weak-students" element={
        <ProtectedRoute>
          <WeakStudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/payables" element={
        <ProtectedRoute>
          <TeacherPayables />
        </ProtectedRoute>
      } />
      <Route path="/teacher/user-guide" element={
        <ProtectedRoute>
          <TeacherUserGuide />
        </ProtectedRoute>
      } />
      <Route path="/parent/user-guide" element={
        <ProtectedRoute>
          <ParentUserGuide />
        </ProtectedRoute>
      } />
      <Route path="/student/user-guide" element={
        <ProtectedRoute>
          <StudentUserGuide />
        </ProtectedRoute>
      } />
      <Route path="/admin/user-guide" element={
        <ProtectedRoute>
          <AdminUserGuide />
        </ProtectedRoute>
      } />
      <Route path="/admin/bills" element={
        <ProtectedRoute>
          <BillApproval />
        </ProtectedRoute>
      } />
      <Route path="/admin/bill-approvals" element={
        <ProtectedRoute>
          <SuperAdminBillApprovals />
        </ProtectedRoute>
      } />
      
      {/* Student Portal Routes */}
      <Route path="/student-portal" element={
        <ProtectedRoute>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/student" element={
        <ProtectedRoute>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/student/tests" element={
        <ProtectedRoute>
          <StudentTests />
        </ProtectedRoute>
      } />
      <Route path="/student/online-tests" element={
        <ProtectedRoute>
          <StudentTestsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/student/rankings" element={
        <ProtectedRoute>
          <PeerRankings />
        </ProtectedRoute>
      } />
      <Route path="/student/homework" element={
        <ProtectedRoute>
          <StudentHomework />
        </ProtectedRoute>
      } />
      <Route path="/student/forum" element={
        <ProtectedRoute>
          <DiscussionForum />
        </ProtectedRoute>
      } />
      <Route path="/student/1on1" element={
        <ProtectedRoute>
          <Student1on1Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/student/doubts" element={
        <ProtectedRoute>
          <StudentDoubts />
        </ProtectedRoute>
      } />
      <Route path="/student/batch-solved-doubts" element={
        <ProtectedRoute>
          <StudentBatchSolvedDoubts />
        </ProtectedRoute>
      } />
      <Route path="/attendance/me" element={
        <ProtectedRoute>
          <MyAttendanceV2 />
        </ProtectedRoute>
      } />
      <Route path="/attendance/report" element={
        <ProtectedRoute>
          <StaffAttendanceReportV2 />
        </ProtectedRoute>
      } />
      <Route path="/student/mastery" element={
        <ProtectedRoute>
          <StudentMasteryPage />
        </ProtectedRoute>
      } />
      <Route path="/student/self-study" element={
        <ProtectedRoute>
          <SelfStudyKit />
        </ProtectedRoute>
      } />
      
      {/* 1-on-1 Classes Routes */}
      <Route path="/1on1/teacher" element={
        <ProtectedRoute>
          <Teacher1on1Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/1on1/parent" element={
        <ProtectedRoute>
          <Parent1on1Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/1on1-management" element={
        <ProtectedRoute>
          <OneOnOneManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/1on1-students" element={
        <ProtectedRoute>
          <OneOnOneStudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/mentoring-report" element={
        <ProtectedRoute>
          <MentoringReport />
        </ProtectedRoute>
      } />
      <Route path="/admin/class-archive" element={
        <ProtectedRoute>
          <ClassArchive />
        </ProtectedRoute>
      } />
      <Route path="/admin/test-schedule" element={
        <ProtectedRoute>
          <TestSchedule />
        </ProtectedRoute>
      } />
      <Route path="/teacher/test-schedule" element={
        <ProtectedRoute>
          <TestSchedule />
        </ProtectedRoute>
      } />
      <Route path="/teacher/class-archive" element={
        <ProtectedRoute>
          <ClassArchive />
        </ProtectedRoute>
      } />
      <Route path="/admin/complaints" element={
        <ProtectedRoute>
          <ComplaintCenter />
        </ProtectedRoute>
      } />
      <Route path="/admin/session-reminders" element={
        <ProtectedRoute>
          <ReminderSettings />
        </ProtectedRoute>
      } />
      
      {/* HRMS Routes */}
      <Route path="/hrms/hr" element={
        <ProtectedRoute>
          <HRPortalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/hrms/employee" element={
        <ProtectedRoute>
          <EmployeeHRMSDashboard />
        </ProtectedRoute>
      } />
      <Route path="/hrms/recruiter" element={
        <ProtectedRoute>
          <RecruiterPortalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/careers" element={<CareersPage />} />

      {/* iter 281: catch-all so a broken/mistyped navigate() never yields a
          silent black screen. Sends the user back to the landing page. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
          <ActiveCenterPill />
          <GlobalHomeButton />
          <GlobalStaffAttendanceGate />
          <TeacherOfMonthCelebration />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;
